import type { ThunkAction } from '@tmapy/redux';
import type { AppData, CatalogData, DetailData, ScrollHistoryState } from './types';
import {
  ENDPOINT,
  EndpointToRedux,
  ENDPOINT_APP_DATA,
  ENDPOINT_CATALOG_DATA,
  ENDPOINT_DETAIL_DATA,
  LOAD_DATA_FAIL,
  LOAD_DATA_START,
  LOAD_DATA_SUCCESS,
  RESET_DATA_QUERY,
  SET_DATA_QUERY,
} from './constants';

export type ActionResetEndpointQuery = ReturnType<typeof actionResetEndpointQuery>;
export type ActionSetEndpointQuery = ReturnType<typeof actionSetEndpointQuery>;

export type ActionLoadDataStart = ReturnType<typeof actionLoadDataStart>;
export type ActionLoadDataSuccess = ReturnType<typeof actionLoadDataSuccess>;
export type ActionLoadDataFail = ReturnType<typeof actionLoadDataFail>;

export type EDASAction =
  | ActionResetEndpointQuery
  | ActionSetEndpointQuery
  | ActionLoadDataFail
  | ActionLoadDataSuccess
  | ActionLoadDataStart;

export const actionResetEndpointQuery = (payload: { endpoint: ENDPOINT }) =>
  ({
    type: RESET_DATA_QUERY,
    payload,
  }) as const;

export const actionSetEndpointQuery = (payload: {
  endpoint: ENDPOINT;
  query: Record<string, string | string[]>;
}) =>
  ({
    type: SET_DATA_QUERY,
    payload,
  }) as const;

const pendingRequests: Record<string, AbortController | null> = {};

export const actionLoadData =
  ({
    endpoint,
    wipe,
    scroll,
    replaceState,
  }: {
    endpoint: ENDPOINT;
    wipe?: boolean;
    scroll?: ScrollHistoryState;
    replaceState?: boolean;
  }): ThunkAction =>
  async (dispatch, getState) => {
    const pendingRequestAbort = pendingRequests[endpoint];
    if (pendingRequestAbort) {
      pendingRequestAbort.abort();
      pendingRequests[endpoint] = null;
    } else {
      dispatch(actionLoadDataStart({ endpoint, wipe, replaceState, scroll }));
    }

    // prescroll
    if (scroll) {
      window.document.body.scrollTo(scroll);
    }

    const state = getState();
    const query = state.edas[EndpointToRedux[endpoint]].query;
    const abortController = new AbortController();
    try {
      const serviceUrlBase = new URL(
        (globalThis as any).EDAS_CONFIG.serviceUrlBase,
        window.location.href,
      ).href;
      const url = new URL(endpoint, serviceUrlBase);
      const searchParams = url.searchParams;
      let isFiltered = false;
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== '' && value.length > 0) {
          isFiltered = true;
          const searchValue = Array.isArray(value) ? value.join(',') : value;
          searchParams.set(key, searchValue);
        }
      }

      pendingRequests[endpoint] = abortController;
      const response = await fetch(url.toString(), {
        headers: {
          accept: 'application/json',
        },
        credentials: 'same-origin',
        signal: abortController.signal,
      });
      if (response.ok) {
        const data = await response.json();
        if (pendingRequests[endpoint] === abortController) {
          if (endpoint === ENDPOINT_CATALOG_DATA) {
            const newUrl = new URL(window.location.href);
            newUrl.search = searchParams.toString();
            const history = window.history;
            if (replaceState) {
              history.replaceState(history.state, '', newUrl.toString());
            } else {
              history.pushState(history.state, '', newUrl.toString());
            }
          }

          pendingRequests[endpoint] = null;
          dispatch(actionLoadDataSuccess({ endpoint, data, isFiltered }));

          // fine-tune scroll
          if (scroll) {
            window.requestAnimationFrame(() => {
              window.document.body.scrollTo(scroll);
            });
          }
        }
      } else {
        throw new Error('Cannot load config');
      }
    } catch (error) {
      if (pendingRequests[endpoint] === abortController) {
        console.error(error);
        pendingRequests[endpoint] = null;
        dispatch(actionLoadDataFail({ endpoint, error }));
      }
    }
  };

export const actionLoadDataStart = (payload: {
  endpoint: ENDPOINT;
  wipe?: boolean;
  replaceState?: boolean;
  scroll?: ScrollHistoryState;
}) =>
  ({
    type: LOAD_DATA_START,
    payload,
  }) as const;

export const actionLoadDataFail = (payload: { endpoint: ENDPOINT; error: unknown }) =>
  ({
    type: LOAD_DATA_FAIL,
    payload,
  }) as const;

export type LoadDataPayload =
  | { endpoint: typeof ENDPOINT_APP_DATA; data: AppData; isFiltered: boolean }
  | { endpoint: typeof ENDPOINT_CATALOG_DATA; data: CatalogData; isFiltered: boolean }
  | { endpoint: typeof ENDPOINT_DETAIL_DATA; data: DetailData; isFiltered: boolean };

export const actionLoadDataSuccess = (payload: LoadDataPayload) =>
  ({
    type: LOAD_DATA_SUCCESS,
    payload,
  }) as const;

export const actionClearFilter = (): ThunkAction => (dispatch) => {
  dispatch(actionResetEndpointQuery({ endpoint: ENDPOINT_CATALOG_DATA }));
  dispatch(actionLoadData({ endpoint: ENDPOINT_CATALOG_DATA }));
};
